import { formatDateShort, formatTimeRange } from "@/lib/utils/formatting";
import { getTimezoneSuffix, getUtcOffsetMinutes } from "@/lib/utils/timezones";
import type { ScheduleItemRow, ScheduleLabelRow } from "../types/db-types";
import { scheduleNow } from "./schedule-clock";

/** Optional time-zone hint passed alongside time-only formatters. `date` anchors DST. */
interface ScheduleTimeFormatOptions {
  timeZone?: string | null;
  /** YYYY-MM-DD anchor for DST disambiguation. Pass the schedule item's date when known. */
  date?: string | null;
}

/**
 * Format a schedule item's time range as a display string.
 * Returns "All Day" if neither start nor end time is provided.
 *
 * When `options.timeZone` is supplied, the live short name (EST/PDT/HKT/...) is appended
 * after the time range. NULL/undefined keeps the legacy zoneless output. `options.date` is
 * used purely to pick the correct DST variant for the abbreviation.
 *
 * @example formatScheduleTime("09:00:00", "10:30:00") // "9 - 10:30 AM"
 * @example formatScheduleTime("09:00:00", "10:30:00", { timeZone: "America/New_York", date: "2026-07-15" }) // "9 - 10:30 AM EDT"
 * @example formatScheduleTime("09:00:00", null) // "9 AM"
 * @example formatScheduleTime(null, null) // "All Day"
 */
export function formatScheduleTime(
  startTime: string | null,
  endTime: string | null,
  options?: ScheduleTimeFormatOptions
): string {
  if (!startTime && !endTime) return "All Day";
  const tzSuffix = getTimezoneSuffix(options?.timeZone, options?.date);
  return `${formatTimeRange(startTime, endTime, " – ")}${tzSuffix}`;
}

/**
 * Format a schedule item's date and time as a combined display string.
 * Includes the date prefix when a date is provided.
 *
 * @example formatScheduleDateTime("2026-03-16", "09:00:00", "10:30:00") // "Mar 16 · 9 - 10:30 AM"
 * @example formatScheduleDateTime("2026-03-16", "09:00:00", "10:30:00", "America/New_York") // "Mar 16 · 9 - 10:30 AM EDT"
 * @example formatScheduleDateTime(null, "09:00:00", null) // "9 AM"
 */
export function formatScheduleDateTime(
  date: string | null,
  startTime: string | null,
  endTime: string | null,
  timeZone?: string | null
): string {
  const timePart = formatScheduleTime(startTime, endTime, { timeZone, date });
  if (!date) return timePart;
  return `${formatDateShort(date)} · ${timePart}`;
}

/**
 * Format a remaining duration as a digital-clock countdown string for the pre-event banner.
 * Hours/minutes/seconds are always two digits; the days segment is included only once the
 * remaining time reaches a full day. Negative inputs clamp to zero ("00h : 00m : 00s").
 *
 * @param remainingMs - Milliseconds until the target instant. Values ≤ 0 render as all-zero.
 * @returns A display string, e.g. "02d : 05h : 20m : 11s" or "05h : 20m : 11s".
 */
export function formatCountdown(remainingMs: number): string {
  const { days, hours, minutes, seconds } = getCountdownParts(remainingMs);
  const pad = (n: number): string => n.toString().padStart(2, "0");
  const segments = [`${pad(hours)}h`, `${pad(minutes)}m`, `${pad(seconds)}s`];
  if (days > 0) segments.unshift(`${pad(days)}d`);
  return segments.join(" : ");
}

/** Days/hours/minutes/seconds remaining until a target instant. Negative input clamps to all-zero. */
export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Break a remaining duration into whole days/hours/minutes/seconds for a segmented countdown
 * display (each number rendered with its own label). Negative inputs clamp to zero.
 *
 * @param remainingMs - Milliseconds until the target instant. Values ≤ 0 yield all zeros.
 * @returns The four time components.
 */
export function getCountdownParts(remainingMs: number): CountdownParts {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/**
 * Resolve label IDs on an item to full label objects.
 *
 * @param labelIds - Array of label IDs assigned to the item.
 * @param labels - All available labels to search through.
 * @returns The matching label objects, preserving the order of `labels`.
 */
export function getItemLabels(labelIds: number[], labels: ScheduleLabelRow[]): ScheduleLabelRow[] {
  return labels.filter((label) => labelIds.includes(label.id));
}

// ============================================================
// Live "now / next" computation (Schedule Day Viewer)
// ============================================================

/** A schedule item resolved to absolute epoch instants in the event's time zone. */
export interface TimedScheduleItem {
  item: ScheduleItemRow;
  /** Start of the item, ms since epoch (UTC). */
  startMs: number;
  /** End of the item, ms since epoch (UTC), or null when the item has no end time. */
  endMs: number | null;
}

/** An upcoming item paired with its countdown, used for the live banner's "NEXT" slides. */
export interface UpcomingScheduleItem {
  item: TimedScheduleItem;
  /** Whole minutes until the item starts, rounded up and floored at 1. */
  startsInMinutes: number;
}

/**
 * Live status of a schedule, computed once from already-resolved instants and a `now`
 * timestamp. Discriminated union so the banner can render the right slide:
 * - `before` — nothing has started yet → "Starts {date}".
 * - `live`   — one or more items are happening now → cycle through them, then through any
 *              `upcoming` items starting within the soon-window ("NEXT · in X min").
 * - `next`   — the event is underway (or hasn't, for callers that don't special-case `before`) but
 *              nothing is live → the next item (`item`/`startsInMinutes`), plus `upcoming`: that
 *              item and any later ones starting within `nextWindowMinutes` of now.
 * - `ended`  — every item has finished (or there are no timed items).
 */
export type ScheduleLiveStatus =
  | { kind: "before"; firstStartMs: number; firstItem: TimedScheduleItem }
  | { kind: "live"; items: TimedScheduleItem[]; upcoming: UpcomingScheduleItem[] }
  | { kind: "next"; item: TimedScheduleItem; startsInMinutes: number; upcoming: UpcomingScheduleItem[] }
  | { kind: "ended" };

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/**
 * Format a whole-minute lead time as a compact "in"-clause label for the live banner, e.g.
 * `Up next · in {label}`. Under an hour it stays in minutes ("45 min"); an hour or more rolls
 * up to hours ("8 hr", "8 hr 2 min") and a day or more to days ("1 day", "2 days 3 hr"), so a
 * large gap reads naturally instead of "482 min". The larger unit is always shown; the smaller
 * unit is appended only when non-zero. Units use "min"/"hr" to match the surrounding banner copy.
 *
 * Precision is intentionally asymmetric: the hour bucket keeps trailing minutes ("8 hr 2 min"), but
 * the day bucket rolls up to whole hours and drops minutes ("2 days 3 hr", never "2 days 3 hr 5
 * min") — a multi-day gap doesn't need minute precision and reads better short.
 *
 * @param minutes - Whole minutes until the item starts (as produced by `computeLiveStatus`).
 * @returns A display label without the leading "in", e.g. "45 min", "8 hr 2 min", "2 days 3 hr".
 */
export function formatStartsIn(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < MINUTES_PER_HOUR) return `${total} min`;

  if (total < MINUTES_PER_DAY) {
    const hours = Math.floor(total / MINUTES_PER_HOUR);
    const mins = total % MINUTES_PER_HOUR;
    return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
  }

  const days = Math.floor(total / MINUTES_PER_DAY);
  const hours = Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const dayLabel = `${days} ${days === 1 ? "day" : "days"}`;
  return hours > 0 ? `${dayLabel} ${hours} hr` : dayLabel;
}

/** Components of a parsed `YYYY-MM-DD` + `HH:MM[:SS]` pair, or null when malformed. */
function parseDateTimeParts(
  date: string,
  time: string
): { y: number; mo: number; d: number; h: number; mi: number; s: number } | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (!dateMatch || !timeMatch) return null;
  const y = Number(dateMatch[1]);
  const mo = Number(dateMatch[2]);
  const d = Number(dateMatch[3]);
  const h = Number(timeMatch[1]);
  const mi = Number(timeMatch[2]);
  const s = Number(timeMatch[3] ?? "0");
  if (![y, mo, d, h, mi, s].every(Number.isFinite)) return null;
  return { y, mo, d, h, mi, s };
}

/**
 * Resolve a schedule item's local wall-clock `date` + `start_time`/`end_time` to absolute
 * epoch instants, interpreting the wall clock in the event's IANA `timeZone`.
 *
 * Uses a two-pass offset correction: the wall-clock parts are first read as if they were UTC
 * (the "provisional" instant), then `getUtcOffsetMinutes` is evaluated **at that provisional
 * instant** (not at "now") to get the zone's offset for the item's own date, and the instant
 * is shifted by that offset. Evaluating the offset at the item's date — rather than today —
 * is what keeps events in a different DST period correct.
 *
 * Known limitation: within the ~1 hour "fall-back" DST window a wall-clock time is ambiguous;
 * this resolves it to the pre-transition offset, which is acceptable for a coarse "now/next"
 * banner. Items with no `date` or no `start_time` are untimed ("Anytime") and return null.
 *
 * @param item - The schedule item.
 * @param timeZone - The event's IANA time zone (must be non-null; callers gate on this).
 * @returns Absolute start/end instants, or null when the item is untimed or malformed.
 */
export function scheduleItemInstant(item: ScheduleItemRow, timeZone: string): TimedScheduleItem | null {
  if (!item.date || !item.start_time) return null;

  const startMs = wallClockToInstant(item.date, item.start_time, timeZone);
  if (startMs === null) return null;

  const endMs = item.end_time ? wallClockToInstant(item.date, item.end_time, timeZone) : null;
  return { item, startMs, endMs };
}

/**
 * Convert a `YYYY-MM-DD` date + `HH:MM[:SS]` wall-clock time in `timeZone` to a UTC epoch (ms).
 * Returns null for malformed input.
 */
export function wallClockToInstant(date: string, time: string, timeZone: string): number | null {
  const parts = parseDateTimeParts(date, time);
  if (!parts) return null;
  const provisionalMs = Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, parts.s);
  const offsetMin = getUtcOffsetMinutes(timeZone, new Date(provisionalMs));
  return provisionalMs - offsetMin * MS_PER_MINUTE;
}

/**
 * Resolve a list of schedule items to timed entries (untimed items dropped) sorted ascending
 * by start instant. Compute this once (e.g. in a `useMemo` keyed on items + timeZone) so the
 * per-tick `computeLiveStatus` only compares numbers.
 *
 * @param items - All schedule items for the event.
 * @param timeZone - The event's IANA time zone.
 * @returns Timed entries sorted by `startMs` ascending.
 */
export function buildTimedItems(items: ScheduleItemRow[], timeZone: string): TimedScheduleItem[] {
  return items
    .map((item) => scheduleItemInstant(item, timeZone))
    .filter((entry): entry is TimedScheduleItem => entry !== null)
    .sort((a, b) => a.startMs - b.startMs);
}

/**
 * Compute the live status of a schedule from pre-resolved, start-sorted timed entries.
 *
 * Order of checks matters: "before the event" (nothing started) is distinguished from
 * "between sessions" (underway, nothing live) — both have an upcoming item, but only the
 * latter is "Up next". An item with no end time is never counted as live (it is a point
 * marker), but its start still drives "before"/"next".
 *
 * @param timed - Timed entries sorted ascending by `startMs` (from `buildTimedItems`).
 * @param now - Current time, ms since epoch.
 * @param upcomingSoonMinutes - Lead-time window (minutes) for the live state's `upcoming` list:
 *   while something is live, items starting within this many minutes are surfaced as "NEXT"
 *   slides. Defaults to 0 (no upcoming surfaced), preserving the prior live-only behavior.
 * @param nextWindowMinutes - Window (minutes) for the `next` state's `upcoming` list when nothing is
 *   live: the next item plus any later items starting within this many minutes of now. Defaults to 0,
 *   which yields just the single next item — preserving behavior for callers that read only `item`.
 * @returns The discriminated live status.
 */
export function computeLiveStatus(
  timed: TimedScheduleItem[],
  now: number,
  upcomingSoonMinutes = 0,
  nextWindowMinutes = 0
): ScheduleLiveStatus {
  if (timed.length === 0) return { kind: "ended" };

  const firstStartMs = timed[0].startMs;
  if (now < firstStartMs) {
    return { kind: "before", firstStartMs, firstItem: timed[0] };
  }

  const live = timed.filter((t) => t.endMs !== null && t.startMs <= now && now < t.endMs);
  if (live.length > 0) {
    const windowMs = upcomingSoonMinutes * MS_PER_MINUTE;
    const upcoming: UpcomingScheduleItem[] =
      windowMs > 0
        ? timed
            .filter((t) => t.startMs > now && t.startMs <= now + windowMs)
            .map((t) => ({ item: t, startsInMinutes: Math.max(1, Math.ceil((t.startMs - now) / MS_PER_MINUTE)) }))
        : [];
    return { kind: "live", items: live, upcoming };
  }

  const next = timed.find((t) => t.startMs > now);
  if (next) {
    const startsInMinutes = Math.max(1, Math.ceil((next.startMs - now) / MS_PER_MINUTE));
    // Surface the next item plus any later items starting within the window (typically the next
    // hour). When the next item is itself beyond the window — or no window was requested — fall
    // back to just that single item so there's always at least one entry.
    const windowMs = nextWindowMinutes * MS_PER_MINUTE;
    const within = windowMs > 0 ? timed.filter((t) => t.startMs > now && t.startMs <= now + windowMs) : [];
    const upcomingTimed = within.length > 0 ? within : [next];
    const upcoming: UpcomingScheduleItem[] = upcomingTimed.map((t) => ({
      item: t,
      startsInMinutes: Math.max(1, Math.ceil((t.startMs - now) / MS_PER_MINUTE)),
    }));
    return { kind: "next", item: next, startsInMinutes, upcoming };
  }

  return { kind: "ended" };
}

// ============================================================
// Day selection (Agenda + Calendar "today" defaults)
// ============================================================

/**
 * The `YYYY-MM-DD` calendar date of an instant as seen in a given IANA time zone.
 * Used to decide whether "today" (in the event's zone) is one of the event days.
 *
 * @param atMs - The instant, ms since epoch.
 * @param timeZone - The IANA time zone to read the date in.
 * @returns The local date string, e.g. "2026-07-15".
 */
export function dateStringInTimeZone(atMs: number, timeZone: string): string {
  // en-CA renders dates as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(atMs));
}

/**
 * Today's date (`YYYY-MM-DD`) as seen in the event's time zone, falling back to the viewer's local
 * zone when the event has none set. Centralizes the schedule's "what counts as today" policy so the
 * Agenda strip, the calendar mobile-default, and the now-line all agree.
 *
 * @param timeZone - Event IANA time zone, or null for a floating-time event.
 * @param atMs - The instant to read. Defaults to the schedule clock (honors the `?__now=` override).
 * @returns Today's local date string in the resolved zone.
 */
export function eventTodayString(timeZone: string | null, atMs: number = scheduleNow()): string {
  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return dateStringInTimeZone(atMs, zone);
}

/**
 * The minute-of-day (0–1439) of an instant as seen in a given IANA time zone. Used to position
 * the calendar "now" line against the hour grid.
 *
 * @param atMs - The instant, ms since epoch.
 * @param timeZone - The IANA time zone to read the wall clock in.
 * @returns Minutes since local midnight (hour * 60 + minute).
 */
export function minuteOfDayInTimeZone(atMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(atMs));
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0;
  return hour * 60 + minute;
}

/**
 * The distinct non-null dates present in date-grouped schedule items, preserving group order.
 *
 * @param groups - Date groups from `groupItemsByDate`.
 * @returns The dated days that have items, in order (excludes the null/"Anytime" group).
 */
export function daysWithItems(groups: { date: string | null }[]): string[] {
  return groups.map((g) => g.date).filter((d): d is string => d !== null);
}

/**
 * Pick the day a day-strip should select by default: today when it is an event day,
 * otherwise the first event day.
 *
 * @param days - The event days that have items (from `daysWithItems`).
 * @param todayStr - Today's date in the event's zone (from `dateStringInTimeZone`), or null.
 * @returns The default day, or null when there are no days.
 */
export function selectDefaultDay(days: string[], todayStr: string | null): string | null {
  if (days.length === 0) return null;
  if (todayStr && days.includes(todayStr)) return todayStr;
  return days[0];
}
